/* Duskhaven product gallery navigation (2026-09-27): orange position dots down the left edge of the
   main product carousel, kept in step with Horizon's <slideshow-component>. Arrows are Horizon's own,
   restyled in dh-gallery-nav.css.
   2026-10-02 (Todd): the arrow pair sits on the photo's own bottom-right corner. Tall photos (4:5) are
   letterboxed inside the wider gallery frame, so the arrows used to land in the dark side margin; place()
   measures where the visible photo ends and sets --dh-arrow-r / --dh-arrow-b on the slideshow container.
   2026-10-04 (Todd: "the arrows stay on the images as they scroll off"): place() used to measure whichever photo
   was most visible on screen, so while a slide animated past, the arrows rode along with it. It now measures the
   photo of the selected (or target) slide relative to its own slide, which doesn't change while the strip
   scrolls, so the arrows stay in the corner during the slide and only move if the next photo's shape differs. */
(() => {
  if (window.__dhGalleryNav) return;
  window.__dhGalleryNav = true;
  const SEL = '.product-information media-gallery.media-gallery--carousel';

  const visibleSlides = (ss) => {
    const list = Array.isArray(ss.slides) ? ss.slides : [...ss.querySelectorAll('slideshow-slide')];
    return list.filter((s) => !s.hidden && s.getAttribute('hidden') === null);
  };

  const setup = (gallery) => {
    const ss = gallery.querySelector('slideshow-component');
    const container = ss && ss.querySelector(':scope > slideshow-container');
    if (!ss || !container || container.querySelector(':scope > .dh-gnav-dots')) return;

    const nav = document.createElement('div');
    nav.className = 'dh-gnav-dots';
    nav.setAttribute('role', 'group');
    nav.setAttribute('aria-label', 'Choose a photo');
    container.appendChild(nav);

    let count = -1;
    const build = () => {
      const slides = visibleSlides(ss);
      if (slides.length === count) return;
      count = slides.length;
      nav.textContent = '';
      nav.hidden = count < 2;
      slides.forEach((_, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'dh-gnav-dot';
        b.setAttribute('aria-label', `Show photo ${i + 1} of ${count}`);
        b.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          try { ss.select(i); } catch (_) {
            const s = visibleSlides(ss)[i];
            if (s) s.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
          }
        });
        nav.appendChild(b);
      });
    };

    // Put the arrows on the bottom-right corner of the selected slide's photo (see the note at the top).
    // Everything is measured relative to the slide itself, so the result is the same mid-scroll and at rest.
    let lastR = '';
    let lastB = '';
    const place = (idx) => {
      const slides = visibleSlides(ss);
      const slide = slides[idx] || slides[0];
      const img = slide && slide.querySelector('img');
      if (!img) return;
      const cr = container.getBoundingClientRect();
      const sr = slide.getBoundingClientRect();
      const r = img.getBoundingClientRect();
      if (!cr.width || !cr.height || !sr.width || !r.width || !r.height) return;
      let cx = r.left - sr.left;
      let cy = r.top - sr.top;
      let cw = r.width;
      let ch = r.height;
      if (getComputedStyle(img).objectFit === 'contain' && img.naturalWidth && img.naturalHeight) {
        const ratio = img.naturalWidth / img.naturalHeight;
        if (r.width / r.height > ratio) { cw = r.height * ratio; cx += (r.width - cw) / 2; }
        else { ch = r.width / ratio; cy += (r.height - ch) / 2; }
      }
      const frameW = Math.min(cr.width, sr.width);
      const frameH = Math.min(cr.height, sr.height);
      const pad = window.matchMedia('(max-width: 749px)').matches ? 10 : 14;
      const right = Math.min(frameW, cx + cw);
      const bottom = Math.min(frameH, cy + ch);
      const vr = Math.max(pad, Math.round(cr.width - right + pad)) + 'px';
      const vb = Math.max(pad, Math.round(cr.height - bottom + pad)) + 'px';
      if (vr !== lastR) { container.style.setProperty('--dh-arrow-r', vr); lastR = vr; }
      if (vb !== lastB) { container.style.setProperty('--dh-arrow-b', vb); lastB = vb; }
    };

    let timer = 0;
    let hinted = null;
    const update = () => {
      timer = 0;
      build();
      let cur = hinted !== null ? hinted : Number(ss.current);
      hinted = null;
      if (!Number.isFinite(cur)) cur = 0;
      place(cur);
      nav.querySelectorAll('.dh-gnav-dot').forEach((d, i) => {
        if (i === cur) d.setAttribute('aria-current', 'true');
        else d.removeAttribute('aria-current');
      });
    };
    // setTimeout rather than requestAnimationFrame, so updates also land in background tabs.
    const schedule = () => { if (!timer) timer = setTimeout(update, 16); };

    ss.addEventListener('slideshow:select', (e) => {
      const i = e && e.detail ? Number(e.detail.index) : NaN;
      if (Number.isFinite(i)) hinted = i;
      schedule();
    });
    ss.addEventListener('scrollend', schedule);
    const slidesEl = ss.querySelector('slideshow-slides');
    if (slidesEl) slidesEl.addEventListener('scroll', schedule, { passive: true });
    ss.addEventListener('load', schedule, true);
    if ('ResizeObserver' in window) new ResizeObserver(schedule).observe(container);
    window.addEventListener('resize', schedule);
    new MutationObserver(schedule).observe(ss, { subtree: true, attributes: true, attributeFilter: ['aria-hidden', 'hidden', 'aria-selected', 'initial-slide'] });
    update();
    setTimeout(schedule, 400);
    setTimeout(schedule, 1500);
  };

  const scan = () => document.querySelectorAll(SEL).forEach(setup);
  let t = 0;
  const rescan = () => { clearTimeout(t); t = setTimeout(scan, 60); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan);
  else scan();
  new MutationObserver(rescan).observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('shopify:section:load', rescan);
})();
